// hooks/useDiscountLogic.js - Enhanced with variant support
import { useState } from 'react';
import { DEFAULT_STORE_ID } from '../../../../../firebase/firebase';
import { addToSales, removeFromSales, getSalesItems } from "../../../../../firebase/sales-functions";
import { getLocalizedProductName } from "../../../../../utils/multilingualUtils";

export const useDiscountLogic = ({
    products,
    salesItems,
    setSalesItems,
    selectedProducts,
    setSelectedProducts,
    getProductDiscount,
    calculateDiscountedPrice,
    setLoading,
    setError
}) => {
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [selectedProductForDiscount, setSelectedProductForDiscount] = useState(null);
    const [discountData, setDiscountData] = useState({
        type: "percentage",
        value: "",
        startDate: "",
        endDate: "",
        description: ""
    });

    // Handle discount modal
    const handleAddDiscount = (product) => {
        setSelectedProductForDiscount(product);
        setDiscountData({
            type: "percentage",
            value: "",
            startDate: new Date().toISOString().split('T')[0],
            endDate: "",
            description: ""
        });
        setShowDiscountModal(true);
    };

    // Enhanced discount submission with variant support
    const handleDiscountSubmit = async (e, submitData = null) => {
        e.preventDefault();

        const finalDiscountData = submitData || discountData;

        if (!finalDiscountData.value || parseFloat(finalDiscountData.value) <= 0) {
            alert("Please enter a valid discount value.");
            return;
        }

        if (finalDiscountData.type === "percentage" && parseFloat(finalDiscountData.value) > 100) {
            alert("Percentage discount cannot be more than 100%.");
            return;
        }

        // For variant discounts, ensure variants are selected
        if (finalDiscountData.target === "variant" && (!finalDiscountData.selectedVariants || finalDiscountData.selectedVariants.length === 0)) {
            alert("Please select at least one variant.");
            return;
        }

        try {
            setLoading(true);

            if (selectedProductForDiscount === "bulk") {
                await processBulkDiscount(finalDiscountData);
            } else {
                await processSingleProductDiscount(selectedProductForDiscount, finalDiscountData);
            }

            // Refresh sales items
            const updatedSalesItems = await getSalesItems(DEFAULT_STORE_ID);
            setSalesItems(updatedSalesItems);

            setShowDiscountModal(false);
            setSelectedProductForDiscount(null);
        } catch (error) {
            console.error("Error adding discount:", error);
            setError("Failed to add discount. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const processBulkDiscount = async (finalDiscountData) => {
        for (const productId of selectedProducts) {
            const product = products.find(p => p.id === productId);
            if (product) {
                await processProductDiscount(product, finalDiscountData, "product", []);
            }
        }
        setSelectedProducts([]);
    };

    const processSingleProductDiscount = async (product, finalDiscountData) => {
        const target = finalDiscountData.target || "product";
        const selectedVariants = finalDiscountData.selectedVariants || [];

        await processProductDiscount(product, finalDiscountData, target, selectedVariants);
    };

    const processProductDiscount = async (product, finalDiscountData, target, selectedVariants) => {
        if (target === "variant") {
            // Apply discount to selected variants
            for (const variantId of selectedVariants) {
                const variant = product.variants.find(v => v.id === variantId);
                if (variant) {
                    await createSaleRecord(product, finalDiscountData, variant);
                }
            }
        } else {
            // Apply discount to entire product
            await createSaleRecord(product, finalDiscountData, null);
        }
    };

    const createSaleRecord = async (product, finalDiscountData, variant = null) => {
        // Remove existing discount if any
        const existingDiscount = getProductDiscount(product.id, variant?.id);
        if (existingDiscount) {
            await removeFromSales(DEFAULT_STORE_ID, existingDiscount.id);
        }

        // Determine the price to use
        const basePrice = variant ? parseFloat(variant.price) : parseFloat(product.price);

        // Calculate discounted price
        const discountedPrice = finalDiscountData.type === "percentage"
            ? basePrice * (1 - parseFloat(finalDiscountData.value) / 100)
            : Math.max(0, basePrice - parseFloat(finalDiscountData.value));

        // Helper function to get clean variant display name
        const getVariantDisplayName = (variant, product) => {
            if (!variant || !product.options) return null;

            const parts = [];
            product.options.forEach(option => {
                const optionKey = typeof option.name === 'string'
                    ? option.name.toLowerCase()
                    : (option.name?.en || Object.values(option.name || {})[0] || '').toLowerCase();

                const value = variant[optionKey];
                if (value) {
                    // Handle multilingual values - extract English for consistent storage
                    let displayValue = value;
                    try {
                        const valueData = JSON.parse(value);
                        if (typeof valueData === 'object' && valueData !== null) {
                            displayValue = valueData.en || Object.values(valueData)[0] || value;
                        }
                    } catch {
                        // Use value as-is if not JSON
                    }

                    const optionName = typeof option.name === 'string'
                        ? option.name
                        : (option.name?.en || Object.values(option.name || {})[0] || 'Option');

                    parts.push(`${optionName}: ${displayValue}`);
                }
            });
            return parts.length > 0 ? parts.join(', ') : null;
        };

        // Create sale data with simplified structure
        const saleData = {
            productId: product.id,
            productName: product.name, // Store full multilingual object
            originalPrice: basePrice,
            discountType: finalDiscountData.type,
            discountValue: parseFloat(finalDiscountData.value),
            discountedPrice: discountedPrice,
            startDate: finalDiscountData.startDate,
            endDate: finalDiscountData.endDate || null,
            description: finalDiscountData.description || `${finalDiscountData.value}${finalDiscountData.type === 'percentage' ? '%' : ' AED'} off`,
            isActive: true,
            createdAt: new Date().toISOString(),
            // Add variant-specific fields if applicable
            ...(variant && {
                variantId: variant.id,
                variantSku: variant.sku,
                variantDisplayName: getVariantDisplayName(variant, product), // Simplified display name
                // Store only essential variant properties for display - cleaner than storing everything
                variantSize: variant.size ? (
                    (() => {
                        try {
                            const sizeData = JSON.parse(variant.size);
                            return typeof sizeData === 'object' ? sizeData.en || Object.values(sizeData)[0] : variant.size;
                        } catch {
                            return variant.size;
                        }
                    })()
                ) : null
            })
        };

        await addToSales(DEFAULT_STORE_ID, saleData);
    };

    // Enhanced remove discount with variant support
    const handleRemoveDiscount = async (productId, variantId) => {
        const discount = getProductDiscount(productId, variantId);

        if (!discount) {
            return;
        }

        const confirmMessage = variantId
            ? "Are you sure you want to remove this variant discount?"
            : "Are you sure you want to remove this discount?";

        if (window.confirm(confirmMessage)) {
            try {
                setLoading(true);
                await removeFromSales(DEFAULT_STORE_ID, discount.id);
                setSalesItems(salesItems.filter(sale => sale.id !== discount.id));
            } catch (error) {
                console.error("Error removing discount:", error);
                setError("Failed to remove discount. Please try again.");
            } finally {
                setLoading(false);
            }
        }
    };

    return {
        showDiscountModal,
        setShowDiscountModal,
        selectedProductForDiscount,
        setSelectedProductForDiscount,
        discountData,
        setDiscountData,
        handleAddDiscount,
        handleDiscountSubmit,
        handleRemoveDiscount
    };
};